import { z } from "zod";
import { auditActorFields } from "../common/audit.js";
import { pagedResponse, paginationQuerySchema } from "../common/pagination.js";
export const STORE_TASK_STATUS = z.enum([
    "open",
    "assigned",
    "in_progress",
    "submitted",
    "completed",
    "verified",
    "rejected",
    "cancelled",
]);
export const STORE_TASK_TYPE = z.enum([
    "restock",
    "move_product",
    "fix_price",
    "place_promo",
    "place_posm",
    "verify_planogram",
    "recapture",
    "review_detection",
    "review_new_product",
    "other",
]);
export const TASK_EVENT_TYPE = z.enum([
    "created",
    "assigned",
    "status_changed",
    "commented",
    "evidence_added",
    "verified",
    "rejected",
    "cancelled",
]);
export const storeTaskInfo = z.object({
    id: z.string().uuid(),
    storeId: z.string().uuid(),
    storeName: z.string().nullable(),
    issueId: z.string().uuid().nullable(),
    parentTaskId: z.string().uuid().nullable(),
    taskType: STORE_TASK_TYPE,
    title: z.string(),
    description: z.string().nullable(),
    priority: z.number().int(),
    status: STORE_TASK_STATUS,
    assignedToUserId: z.string().uuid().nullable(),
    assigneeEmail: z.string().nullable(),
    dueAt: z.string().datetime().nullable(),
    startedAt: z.string().datetime().nullable(),
    submittedAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
    verifiedAt: z.string().datetime().nullable(),
    rejectionReason: z.string().nullable(),
    /**
     * Capture this task was generated from, for auto-created tasks. Lets the
     * scan UI ask "what did this photo turn up?" and the review screen link
     * back to the shelf it came from. Null for manually-created tasks.
     */
    captureId: z.string().uuid().nullable(),
    /**
     * The product proposal being reviewed (`review_new_product` tasks). Carries
     * the vision `auto_search` block — brand/variant/size, a web product image,
     * the source link, a confidence score, and the shelf crop — in its own
     * metadata. Null for every other task type.
     */
    variantCandidateId: z.string().uuid().nullable(),
    metadata: z.record(z.unknown()),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    ...auditActorFields,
});
export const createStoreTaskRequest = z.object({
    storeId: z.string().uuid(),
    issueId: z.string().uuid().nullish(),
    parentTaskId: z.string().uuid().nullish(),
    taskType: STORE_TASK_TYPE.default("other"),
    title: z.string().min(1).max(500),
    description: z.string().max(5000).nullish(),
    priority: z.number().int().min(1).max(5).default(3),
    assignedToUserId: z.string().uuid().nullish(),
    dueAt: z.string().datetime().nullish(),
    metadata: z.record(z.unknown()).default({}),
});
export const updateStoreTaskRequest = z
    .object({
    status: STORE_TASK_STATUS.optional(),
    taskType: STORE_TASK_TYPE.optional(),
    title: z.string().min(1).max(500).optional(),
    description: z.string().max(5000).nullish(),
    priority: z.number().int().min(1).max(5).optional(),
    assignedToUserId: z.string().uuid().nullish(),
    dueAt: z.string().datetime().nullish(),
    rejectionReason: z.string().max(2000).nullish(),
    metadata: z.record(z.unknown()).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "At least one field required" });
export const listStoreTasksQuery = paginationQuerySchema.extend({
    storeId: z.string().uuid().optional(),
    status: STORE_TASK_STATUS.optional(),
    taskType: STORE_TASK_TYPE.optional(),
    assignedToUserId: z.string().uuid().optional(),
    issueId: z.string().uuid().optional(),
    /** Everything a single scan turned up — drives the capture-analysis card. */
    captureId: z.string().uuid().optional(),
});
export const storeTasksListResponse = pagedResponse(storeTaskInfo);
export const addTaskEventRequest = z.object({
    eventType: TASK_EVENT_TYPE,
    note: z.string().max(2000).nullish(),
    payload: z.record(z.unknown()).default({}),
});
export const taskEventInfo = z.object({
    id: z.number().int(),
    taskId: z.string().uuid(),
    eventType: TASK_EVENT_TYPE,
    oldStatus: STORE_TASK_STATUS.nullable(),
    newStatus: STORE_TASK_STATUS.nullable(),
    actorUserId: z.string().uuid().nullable(),
    note: z.string().nullable(),
    payload: z.record(z.unknown()),
    createdAt: z.string().datetime(),
});
//# sourceMappingURL=store-tasks.schemas.js.map